import { isPlatformBrowser, NgOptimizedImage } from '@angular/common';
import { AfterViewChecked, ChangeDetectorRef, Component, effect, ElementRef, inject, NgZone, OnInit, PLATFORM_ID, QueryList, TemplateRef, ViewChild, ViewChildren, ChangeDetectionStrategy, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { EMPTY, finalize, map, Observable, startWith, Subscription, switchMap, tap } from 'rxjs';
import { AuthService } from '../../auth/auth.service';
import { CookieService } from '../../auth/cookie.service';
import { ChatService } from '../../services/chat/chat.service';
import { ToasterService } from '../../shared/services/toaster.service';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { AskQuestionModel, chat, ContentBlock, messages, Source, TeeTimeActionPayload } from '../../model/chat.model';
import { BlockElementsComponent } from '../answer/block-elements/block-elements.component';
import { FormatChatPipe } from '../../pipes/format-chat/format-chat.pipe';
import { RuleAssistModalComponent } from '../../shared/components/rule-assist-modal/rule-assist-modal.component';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { SharedService } from '../../shared/services/shared.service';
import { v4 as uuidv4 } from 'uuid';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MediaService } from '../../services/media.service';
import { MediaModel } from '../../model/media.model';
import { SourceBlockComponent } from '../../shared/components/sources/source-block/source-block.component';
import { SourceModalComponent } from '../../shared/components/sources/block-modal/block-modal.component';
import { SiteLogoService } from '../../shared/services/site-logo.service';
import { BackButton } from '../../model/shared.model';
import { TokenService } from '../../auth/token.service';
import { PersonalizationService } from '../../services/personalization.service';
import { BookingService } from '../../services/booking.service';
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-chat-box',
  imports: [
    NgOptimizedImage,
    FormsModule,
    BlockElementsComponent,
    SourceBlockComponent,
    SourceModalComponent,
    FormatChatPipe,
    TranslatePipe,
    RuleAssistModalComponent,
    RouterLink
  ],
  providers: [TranslatePipe],
  templateUrl: './chat-box.component.html',
  styleUrl: './chat-box.component.scss'
})
export class ChatBoxComponent implements OnInit, AfterViewChecked {
  private _subscription: Subscription = new Subscription();
  private _ngZone = inject(NgZone);
  private _chatService = inject(ChatService);
  private _bookingService = inject(BookingService);
  private _toaster = inject(ToasterService);
  private _translatePipe = inject(TranslatePipe);
  private _cookieService = inject(CookieService);
  private _authService = inject(AuthService);
  private _changeDetect = inject(ChangeDetectorRef);
  private _modalService = inject(NgbModal);
  private _sharedService = inject(SharedService);
  private _router = inject(Router);
  private _mediaService = inject(MediaService);
  private _activatedRoute = inject(ActivatedRoute);
  private _siteLogoService = inject(SiteLogoService);
  private _tokenService = inject(TokenService);
  protected readonly personalization = inject(PersonalizationService);
  private lastCookieSetTime: number = 0; // Timestamp of the last cookie set
  private cookieExpiryDuration: number = 90 * 1000; // 1.5 minutes in milliseconds
  private ruleAssistModalRef!: NgbModalRef;
  @ViewChildren('questionHeader') headers!: QueryList<ElementRef>;
  @ViewChild('scrollContainer') private scrollContainer!: ElementRef;
  chatPrompt = signal('');
  processing = signal(false);
  loopMessages: chat[] = [];
  /** Quick-start prompts shown when the chat is empty. `label` is displayed, `text` is sent. */
  readonly chatSuggestions: { label: string; text: string }[] = [
    { label: 'Give me a golf tip of the day 🏌️', text: 'Give me a golf tip of the day' },
    { label: 'Quiz me on golf rules 📖', text: 'Quiz me on golf rules' },
    { label: 'Tell me a golf fact 💡', text: 'Tell me a golf fact' },
  ];
  receivingMessage = signal(false);
  textOnly = signal(true);
  answerGenerating = signal(false);
  chatId = signal('');
  isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  followUpQuestion = signal('');
  medias: MediaModel[] = [];
  isMyBag = signal(false);
  messageSubscribed = signal(false);
  processingText = signal(this._translatePipe.transform('Generating...'));
  private buffer = '';
  loading = signal(false);
  answerProcessing = signal(false);
  ruleAssistQuestionObject!: chat;
  answerListening = signal(false);
  audioAnswerLoading = signal(false);
  isSignedIn = signal(false);
  currentChatPage = signal(1);
  isNewSharedChat = signal(false);
  showLoadMoreBtn = signal(false);
  loadingMoreHistory = signal(false);
  continueChatProgress = signal(false);
  chatLoading = signal(false);
  question: AskQuestionModel = new AskQuestionModel();
  isSharedChat = signal(false);
  sharedChatTriggered = signal(false);
  expandInput = signal(false);

  @ViewChild('equalizerCanvas') canvasRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('audioRef') audioRef!: ElementRef<HTMLAudioElement>;
  @ViewChild('chatTextarea') chatTextareaRef!: ElementRef<HTMLTextAreaElement>;
  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];
  isRecording = signal(false);
  recordingStatus = signal('Not recording');
  base64Audio = signal<string | null>(null);
  showEqualizer = signal(false);
  recordingFinished = signal(false);
  mediaElementSource: MediaElementAudioSourceNode | null = null;
  audioContext!: AudioContext | null;
  analyser!: AnalyserNode;
  dataArray: Uint8Array<ArrayBuffer> | null = null;
  animationFrameId: number | null = null;
  isPlaying = signal(false);
  audioUrl = signal<string | null>(null);
  audioBlob: Blob | undefined;
  totalMessages = signal(0);
  openChatBox = signal(false);
  showSourceModal = signal(false);
  selectedSources: Source[] = [];
  hideLoadingText = signal(false);
  prevButtonAction = new BackButton();
  isAudioPlaying = signal(false);
  submittingAudio = signal(false);
  sourceModalRef: NgbModalRef | undefined;
  private autoScrollEnabled = true;

  publicLimitReached = signal(true);

  constructor() {
    effect(() => {
      const buttonAction = this._sharedService.backButtonAction();
      if (buttonAction && buttonAction.action === 'chatBox') {
        this.closeChatPop();
        setTimeout(() => {
          this._sharedService.backButton.set(this.prevButtonAction);
        }, 100);
      }
    });

    // NOTE: this effect must track ONLY `loggedIn()`. It previously read
    // `isSignedIn()` and wrote to it in the same body, which made the effect
    // dirty its own dependency and re-run — firing `latest-chat-id`,
    // `message?chat=...`, setCookie and the websocket handshake twice on every
    // sign-in. Everything after the read is wrapped in `untracked()` so no
    // other signal (isSignedIn, isSharedChat) can re-trigger it either.
    effect(() => {
      const signedIn = this._sharedService.loggedIn();

      untracked(() => {
        const previousSignedInState = this.isSignedIn();
        this.isSignedIn.set(signedIn);
        const currentPath = this._router.url.split('?')[0];
        const isAuthRoute = currentPath === '/login' || currentPath === '/signup';

        // Reset component if user was signed in but now is not
        if (previousSignedInState && !signedIn) {
          this.resetComponentState();
        }

        // Do not trigger chat fetches on auth routes
        if (!isAuthRoute && !this.isSharedChat() && signedIn) {
          this.getLatestChatId();
        }
        if (!isAuthRoute && this.isBrowser) {
          this.setCookieIfNeeded().subscribe({
            next: () => {
              this._ngZone.runOutsideAngular(() => {
                this._chatService.onListenWebsocket()
                  .then(() => {
                    this._chatService.getMessageHistory();
                    this._chatService.checkQuestionLimit();
                  })
                  .catch((error) => {
                    console.log(error);
                  });
              });
            }
          });
        }
      });
    });

    effect(() => {
      let messages = this._chatService.publicQuestionHistory();
      if(messages.length && !this.isSignedIn()){
        // Pre-process messages to handle JSON content from the assistant
        // Pre-process messages to handle JSON content from the assistant
        messages = messages.filter(message => message.role !== 'system');
        messages.forEach(message => {
          if (message.role === 'assistant' && message.content) {
            try {
              const parsedContent = JSON.parse(message.content);
              if (Array.isArray(parsedContent)) {
                // Handle array of content blocks
                message.contentBlocks = parsedContent.map(b => this.maybeConvertEmptyListToBookingForm(b));
                message.isBlocks = true;
                message.content = '';
              } else if (typeof parsedContent === 'object' && parsedContent !== null) {
                // Handle a single content block object
                message.contentBlocks = [this.maybeConvertEmptyListToBookingForm(parsedContent)];
                message.isBlocks = true;
                message.content = '';
              }
            } catch (e) {
              // Not a JSON string, do nothing.
            }
          }
        });
        this.processMessages({
          results: messages, 
          totalPages: 1, 
          totalResults: messages.length, 
          page: 1, 
          limit: 10,
          type: 'NEW',
          chatId: ''
        });
      }
    });

    effect(() => {
      this.publicLimitReached.set(this._chatService.publicQuestionLimitExceeded());
    });
  }

  ngOnInit(): void {
    if (this.isBrowser) {
      if (window.innerWidth <= 991){
        this.setupIOSKeyboardFix();
      } else {
        this.setupGlobalKeyListener();
      }
    this._activatedRoute.queryParams.subscribe(params => {
      // this.testing = JSON.stringify(params);
      if (params['id']) {
        this.chatId.set(params['id']);
        if (params['regenerate'] == 'true') {
          this._sharedService.question.subscribe(res => {
            const q = res as AskQuestionModel;
            this.loopMessages.push({ question: q.textQuestion, answer: '', loading: true, id: '' });
            this.askQuestion(q, true);
            this.getMessages();
          })
        } else {
          this.chatLoading.set(true);
          this.getMessagesFromId();
        }
      } else if (params['sharedId']) {
        this.chatLoading.set(true);
        this.chatId.set(params['sharedId']);
        this.isSharedChat.set(true);
        if (!this.sharedChatTriggered()) {
          this.getMessagesFromSharedId();
        }
        // this.clearQueryParams();
      } else {
        if (params['question']) {
          this.question.textQuestion = params['question'];
          this.answerProcessing.set(true);
          this.startChat(this.question);
          this.getMessages();
        } else if (params['myBag']) {
          this.isMyBag.set(true);
          this.question.textQuestion = 'My Bag Analysis';
          this.question.processType = 'QA-MY-BAG';
          this.loopMessages.push({ question: this.question.textQuestion, answer: '', loading: true, id: '' });
          // this.startChat(this.question, false);
          this._chatService.askQuestionV2(this.question);
          this.answerProcessing.set(true);
          this.getMessages();
        } else {
          // if(this.previousUrl?.includes("discover")){
          //   this._sharedService.articleData$.subscribe(res => {
          //     this.loopMessages.push({
          //       question: res.title,
          //       answer: res.description,
          //       loading: false,
          //       id: res.id
          //     })
          //   })
          // } else{
          this._subscription.add(
            this._sharedService.question.subscribe(res => {
              if (res && typeof res !== 'string' && res.textQuestion) {
                if(this.publicLimitReached() && !this.isSignedIn()){
                  this._toaster.warning('You have exceeded the public chat limit. Please login or signup to continue.', this._translatePipe.transform("Warning"));
                  return;
                }
                this.question = res;
                if (this.question.audioQuestion || this.question.mediaKeys.length) {
                  this.textOnly.set(false);
                }
                this.chatLoading.set(true);
                if (this.answerProcessing()){
                  return;
                }
                // Tile-clicks and other external dispatchers always land here.
                // Route directly through askQuestion/startChat — calling getChatResponse
                // would flip hideLoadingText=true and expand the textarea, which we
                // don't want for click-dispatched questions.
                this.chatPrompt.set('');
                this.answerProcessing.set(true);
                const qm = new AskQuestionModel();
                qm.chatId = this.chatId();
                qm.textQuestion = res.textQuestion;
                qm.separation = res.separation;
                qm.behaviour = res.behaviour ?? 'clicked';
                qm.showProcessingText = res.showProcessingText;
                if (res.action) {
                  qm.action = res.action;
                }
                this.openMobileChatBoxIfNeeded();
                if (this.chatId()) {
                  this.askQuestion(qm);
                } else {
                  this.startChat(qm);
                }
                this.getMessages();
              }
            })
          );

          //region Test question
          // if (this.isBrowser) {
          //   const demoQuestion = new AskQuestionModel();
          //   demoQuestion.textQuestion = 'create an article about golf which includes three charts that is in different types and table of a famous player recent scores';
          //   this.question = demoQuestion;
          //   if (this.question.audioQuestion || this.question.mediaKeys.length) {
          //     this.textOnly = false;
          //   }
          //   this.startChat(this.question);
          //   this.answerProcessing = true;
          //   this.getMessages();
          // }
          
          // this._sharedService.question.next(demoQuestion);
        }
      }
    });
    }

    this._subscription.add(
      this._sharedService.openTeeTimeBookingForm$.subscribe(() => {
        this.openLocalTeeTimeBookingForm();
      })
    );

    this._subscription.add(
      this._chatService.audioEnded().subscribe(res => {
        if (res === true) {
          this.answerListening.set(false);
          this.loopMessages.map(item => {
            item.listening = false;
            item.loadingAudio = false; // Clear loading flag for all messages when audio ends
          });
        } else if (res === false) {
          this.audioAnswerLoading.set(false);
          // Clear loading flag for all messages when audio fails to load
          this.loopMessages.map(item => item.loadingAudio = false);
        }
        this._changeDetect.markForCheck();
      })
    );
    
  }

  /**
   * Second line of defence against duplicate history loads: even if the effect
   * above is re-entered (route change, future refactor), only one
   * `latest-chat-id` request is in flight at a time.
   */
  private latestChatIdInFlight = false;

  getLatestChatId() {
    if (this.latestChatIdInFlight) {
      return;
    }
    this.latestChatIdInFlight = true;
    this._chatService.getLatestChatId().subscribe({
      next: res => {
        this.latestChatIdInFlight = false;
        this.chatId.set(res.chatId);
        this.chatLoading.set(true);
        this.getMessagesFromId();
      },
      error: () => {
        this.latestChatIdInFlight = false;
      }
    });
  }

  getMessagesFromSharedId() {
    if (!this.sharedChatTriggered() || this.loadingMoreHistory()) {
      this.sharedChatTriggered.set(true);
      if (this.isBrowser) {
        // this._sharedService.loading.set(false);
      }
      this._chatService.getMessageFromSharedId(this.chatId(), 10, this.currentChatPage()).subscribe({
        next: ((rawRes) => {
          const res = rawRes as messages;
          res.results.forEach(element => {
            const isJson = this.isJson(element.content);
            if (isJson) {
              const parsed = JSON.parse(element.content);
              element.contentBlocks = (Array.isArray(parsed) ? parsed : [parsed])
                .map((b: ContentBlock) => this.maybeConvertEmptyListToBookingForm(b));
              element.isBlocks = true;
              element.content = '';
            } else {
              element.contentBlocks = [];
              element.isBlocks = false;
            }
          });
          this.answerProcessing.set(false);
          this.processMessages(res, true);
        })
      })
    }
  }

  getMessagesFromId() {
    this._chatService.getMessageFromId(this.chatId(), 10, this.currentChatPage()).subscribe({
      next: ((res: messages) => {
        this.totalMessages.set(res.totalPages);
        res.results.forEach(element => {
          this.totalMessages.set(res.totalResults);
          const isJson = this.isJson(element.content);
          if (isJson) {
            const parsed = JSON.parse(element.content);
            element.contentBlocks = (Array.isArray(parsed) ? parsed : [parsed])
              .map((b: ContentBlock) => this.maybeConvertEmptyListToBookingForm(b));
            element.isBlocks = true;
          } else {
            element.contentBlocks = [];
            element.isBlocks = false;
          }
        });
        this.answerProcessing.set(false);
        this.processMessages(res);
      }),
      error: ((res: unknown) => {
        const currentPath = this._router.url.split('?')[0];
        const isAuthRoute = currentPath.startsWith('/login') || currentPath.startsWith('/signup');
        if (!isAuthRoute) {
          this._router.navigateByUrl('/');
        }
      })
    })
  }

  showInputBox(){
    if (this.answerProcessing()){
      return;
    }
    this.hideLoadingText.set(true);

    this._changeDetect.markForCheck();
  }

  hideProcessingText(){
    if (!this.chatPrompt().trim()){
      this.hideLoadingText.set(false);
    }
  }

  /**
   * When the input loses focus, collapse the chat box back to its default
   * 1-line state — but only if the user hasn't typed anything and isn't busy
   * recording/submitting audio. If focus moved to another control inside the
   * chat box (mic, send, etc.) we keep it expanded.
   */
  onChatInputBlur(event: FocusEvent){
    const nextFocused = event.relatedTarget as HTMLElement | null;
    if (nextFocused?.closest('.voice-block')) {
      return;
    }
    if (!this.chatPrompt().trim() && !this.isRecording() && !this.submittingAudio()) {
      this.hideLoadingText.set(false);
      this._changeDetect.markForCheck();
    }
  }

  /** Open the mobile chat-box panel without expanding the input area (no hideLoadingText flip). */
  private openMobileChatBoxIfNeeded(): void {
    if (typeof window !== 'undefined' && window.innerWidth < 991 && !this.openChatBox()) {
      document.body.classList.add('fixed');
      this.openChatBox.set(true);
      this.prevButtonAction = this._sharedService.backButton();
      this._sharedService.backButton.set({
        show: true,
        url: '/',
        action: 'chatBox',
        text: '',
      });
    }
  }

  setOpenChatBox(){
    this.hideLoadingText.set(true);
    if (typeof window !== 'undefined' && window.innerWidth < 991 && !this.openChatBox()) {
      document.body.classList.add('fixed');
      this.openChatBox.set(true);
      this.prevButtonAction = this._sharedService.backButton();
      this._sharedService.backButton.set({
        show: true,
        url: '/',
        action: 'chatBox',
        text: ''
      });
    }
  }

  autoResize(event: Event) {
    const textarea = event.target as HTMLTextAreaElement;

    // Reset inline height so scrollHeight reflects the actual content height
    textarea.style.height = 'auto';

    const style = window.getComputedStyle(textarea);
    const lineHeight = parseInt(style.lineHeight) || 20;
    const lines = Math.round(textarea.scrollHeight / lineHeight);

    if (lines > 1) {
      // Grow only when the text wraps to a second line or more
      textarea.style.height = textarea.scrollHeight + 'px';
    } else {
      // Single line — fall back to the CSS min-height (no inline height)
      textarea.style.height = '';
    }
  }
  

  processMessages(res: messages, isShared = false) {
    if (res.type === 'NEW' || !isShared) {
      if (res.type === 'NEW') {
        this.isNewSharedChat.set(true);
      }
      if (res.totalPages > 1) {
        this.showLoadMoreBtn.set(true);
      }
      if (!res.results.length) {
        this.showLoadMoreBtn.set(false);
      }
      if (this.loadingMoreHistory()) {
        res.results.filter(rs => rs.role === 'user').forEach((item, index) => {
          if (item.media && item.media.length) {
            const md = item.media.filter(mm => mm);
            this.getMediaUrl(md);
          }
        });
        res.results.filter(rs => rs.role === 'assistant').forEach((item, index) => {
          if (item.role === 'assistant') {
            const questionIndex = res.results.findIndex(lm => lm.id === item.questionRef);
            if (questionIndex !== -1) {
              this.loopMessages.unshift({
                answer: item.content,
                contentBlocks: item.contentBlocks,
                isBlocks: item.isBlocks,
                question: res.results[questionIndex].content,
                loading: false,
                id: res.results[questionIndex].id,
                route: item.route,
                sources: item.sources,
                answerId: item.id,
                reaction: item.reaction
              });
            }
          }
        });

        // res.results.forEach((item) => {
        //   if(item.role === 'assistant'){
        //     questionIndex = 0;
        //     this.loopMessages.unshift({ question:  '', answer: item.content, loading: false });           
        //   } else if (item.role === 'user'){
        //     this.loopMessages[questionIndex].question = item.content;
        //   }
        // });

        if (res.totalResults === (this.loopMessages.length * 2)) {
          this.showLoadMoreBtn.set(false);
        }
        this.loadingMoreHistory.set(false);
      } else {
        if (!this.isSignedIn()){
          res.results.forEach((item, index) => {
            if (item.role === 'assistant') {
              this.loopMessages.push({
                answer: item.content,
                contentBlocks: item.contentBlocks,
                isBlocks: item.isBlocks,
                question: res.results[index - 1].content,
                loading: false,
                id: item.id,
                route: item.route,
                sources: item.sources,
                answerId: item.id,
                reaction: item.reaction
              });
            }
          });
        } else {
          res.results = res.results.reverse();
          res.results.filter(rs => rs.role === 'user').forEach((item, index) => {
            if ( item.media && item.media.length) {
              const md = item.media.filter(mm => mm);
              this.getMediaUrl(md);
            }
          });
  
          let previousRoute: string | undefined = undefined;
          res.results.filter(rs => rs.role === 'assistant').forEach((item, index) => {
            if (item.role === 'assistant') {
              const questionIndex = res.results.findIndex(lm => lm.id === item.questionRef);
              if (questionIndex !== -1) {
                const message:chat = {
                  answer: item.content,
                  contentBlocks: item.contentBlocks,
                  isBlocks: item.isBlocks,
                  question: res.results[questionIndex].content,
                  loading: false,
                  id: res.results[questionIndex].id,
                  sources: item.sources,
                  route: item.route,
                  answerId: item.id,
                  reaction: item.reaction
                };
                // 🆕 Add `separation` if the route has changed
                if (item.route !== previousRoute) {
                  message.separation = item.route;
                  previousRoute = item.route;
                }
  
                this.loopMessages.push(message);
              }
            }
          })
        }       

        // res.results.forEach((item, index) => {
        //   if(item.role === 'user'){
        //     index === 0 ? questionIndex = 0 : questionIndex++;
        //     this.loopMessages.push({ question:  item.content, answer: '', loading: false });       
        //   } else if (item.role === 'assistant'){
        //     this.loopMessages[questionIndex].answer = item.content;
        //   }
        // });

        setTimeout(() => {
          this.scrollToBottom();
        }, 100);
        this._changeDetect.markForCheck();
      }
      if (this.continueChatProgress()) {
        this.question.textQuestion = this.followUpQuestion();
        this.askQuestion(this.question);
        this.continueChatProgress.set(false);
      }
      this.loadingMoreHistory.set(false);
      this.chatLoading.set(false);
      this.getMessages();
      
    } else if (res.type === 'OWNER' || res.type === 'FOLLOWER') {
      this._router.navigate(['answer'], { queryParams: { id: res.chatId } });
    }
    this.updateSourceImage();
    this._changeDetect.markForCheck();
  }

  updateSourceImage() {
    // Flatten all sources from loopMessages
    const allSources = this.loopMessages.flatMap(msg => msg.sources || []);
  
    // Use service to refresh logos
    this._siteLogoService.refreshLogosForSources(allSources).subscribe({
      next: (updatedSources) => {
        // Build a Map of sourceId -> fresh image URL
        const sourceImageMap = new Map(
          updatedSources.map(source => [source._id, source.image])
        );
  
        // Update images in loopMessages
        this.loopMessages.forEach(res => {
          if (res.sources){
            res.sources.forEach((source: Source) => {
              source.image = sourceImageMap.get(source._id) || 'assets/logos/default.png';
            });
          }
        });
      },
      error: (err) => {
        console.error('Failed to refresh source images', err);
        // Fallback: keep old images
      }
    });
  }
  

  continueChat(question: string) {
    if (question.trim()) {
      this._chatService.continueChat(this.chatId()).subscribe({
        next: (rawRes => {
          const res = rawRes as { chatId: string };
          this.chatId.set(res.chatId);
          this.continueChatProgress.set(true);
          this._router.navigate(['answer'], { queryParams: { id: res.chatId } });
        })
      })
    } else {
      this._toaster.warning(this._translatePipe.transform("QuestionRequired"), this._translatePipe.transform("Warning"));
    }
  }

  getMediaUrl(ids: string[]) {
    this._mediaService.getMedia(ids).subscribe({
      next: (res => {
        this.medias = res;
      })
    })
  }

  isJson(content: string): boolean{
    let parsedContent: unknown;
    let isJson = false;

    try {
      const maybeJson = typeof content === 'string' ? JSON.parse(content) : content;
      if (Array.isArray(maybeJson)) {
        parsedContent = maybeJson;
        isJson = true;
      }
    } catch {
      // Not valid JSON, treat as markdown
      isJson = false;
    }

    return isJson;
  }

  closeChatPop() {
    this.openChatBox.set(false);
    document.body.classList.remove('fixed');
    this.hideLoadingText.set(false);
  }

  nonLoggedInToaster(){
    const loggedIn = this._tokenService.isSignedIn();
    if(!loggedIn){
      this.chatPrompt.set('');
      // Clear the question from shared service to prevent infinite loops
      this._sharedService.setQuestion(new AskQuestionModel());
      this._toaster.warning('Please login to ask a question', this._translatePipe.transform("Warning"));
      this._router.navigate(['/login']);
    }
  }

  /** Fill the input with a suggested prompt and send it. */
  sendSuggestion(text: string) {
    if (this.answerProcessing()) {
      return;
    }
    this.chatPrompt.set(text);
    this.getChatResponse();
  }

  getChatResponse(separation = '') {
    // if (!this.isSignedIn){
    //   this.nonLoggedInToaster();
    //   this._router.navigate(['/login']);
    //   return;
    // }
    if(this.audioBlob || this.chatPrompt().trim()){
      this.setOpenChatBox();
    }
    if(this.audioBlob){
      this.submitAudioQuestion();
      this.answerProcessing.set(true);
    } else if (this.chatPrompt().trim()) {
      this.answerProcessing.set(true);
      let questionModel = new AskQuestionModel();
      questionModel.textQuestion = this.chatPrompt();
      questionModel.separation = separation;
      this.chatPrompt.set('');
      // this.focusInput();
      // this.hideProcessingText();
      if(this.chatId()){
        this.askQuestion(questionModel);
      } else {
        this.startChat(questionModel);
      }
      this.getMessages();
    }
  }
//region GET MESSAGE
  getMessages(): void {
    if (!this.messageSubscribed()) {
      this.messageSubscribed.set(true);
      this._subscription.add(
        this._chatService.getMessages().subscribe({
          next: (message) => {
          if(message.source && message.source === 'discovery') {
            return;
          }
          if (message.event === 'ANSWER_CHAT_ID') {
            this.chatId.set(message.data as string);
          } else if (message.event === "GETTING CONTEXT") {
            this.processingText.set(this._translatePipe.transform('Generating...'));
          } else if (message.event === 'STATUS' && message.data && typeof message.data !== 'string' && (message.data as Record<string, unknown>)['message']) {
            this.processingText.set((message.data as Record<string, unknown>)['message'] as string);
          } else if (message.event === 'ANSWER_TEXT_CHUNK') {
            this.answerProcessing.set(true);
            if (!this.receivingMessage()) {
              this.receivingMessage.set(true);
              this.buffer = '';
            }
            this.buffer += message.data;
            this.loading.set(false);
            this.loopMessages[this.loopMessages.length - 1].loading = false;
            this.loopMessages[this.loopMessages.length - 1].showProcessingText = false;
            this.updateMessages(this.buffer);
            this.adjustScrollForLastResponse();
          } else if (message.event === "ANSWER_SOURCE") {
            this.loopMessages[this.loopMessages.length - 1].sources = message.data as unknown as Source[];
            this.updateSourceImage();
          } else if (message.event === "ROUTE_INFO") {
            this.loopMessages[this.loopMessages.length - 1].route = message.data as string;
            this.loopMessages[this.loopMessages.length - 1].separation = message.data as string;
          } else if (message.event === 'ANSWER_ERROR') {
            const errorText = message.error || 'Something went wrong. Please try again.';
            const lastMessage = this.loopMessages[this.loopMessages.length - 1];
            if (lastMessage) {
              lastMessage.loading = false;
              lastMessage.showProcessingText = false;
              lastMessage.answerProcessing = false;
              // The error is surfaced via the toaster below — don't render it as a
              // chat bubble. If nothing streamed before the failure, hide the empty
              // assistant turn so we don't leave a blank answer + reaction row.
              const hasStreamedContent =
                (lastMessage.isBlocks && (lastMessage.contentBlocks?.length ?? 0) > 0)
                || !!lastMessage.answer?.trim();
              if (!hasStreamedContent) {
                lastMessage.errored = true;
              }
            }
            this.answerProcessing.set(false);
            this.answerGenerating.set(false);
            this.receivingMessage.set(false);
            this.buffer = '';
            this.processingText.set('');
            this._toaster.error(errorText, "Error");
            this._changeDetect.markForCheck();
          } else if (message.event === "ANSWER_OBJECT") {
            this.answerProcessing.set(true);
            this.loopMessages[this.loopMessages.length - 1].loading = false;
            this.loopMessages[this.loopMessages.length - 1].showProcessingText = false;
            this.loopMessages[this.loopMessages.length - 1].isBlocks = true;
            this.loopMessages[this.loopMessages.length - 1].answerProcessing = true;
            const lastMessage = this.loopMessages[this.loopMessages.length - 1];
            const incomingBlock = message.data as unknown as ContentBlock;
            const blockToAppend = this.maybeConvertEmptyListToBookingForm(incomingBlock);
            if (lastMessage.contentBlocks) {
              lastMessage.contentBlocks = [...lastMessage.contentBlocks, blockToAppend];
            } else {
              lastMessage.contentBlocks = [blockToAppend];
            }
            // Refresh the cached bookings list whenever a booking or cancellation
            // is confirmed in chat — the landing page's next-booking card and the
            // /tee-times list both read from BookingService.bookings(), so this
            // keeps them in sync without forcing the user to re-navigate.
            if (blockToAppend.type === 'booking-confirmation' || blockToAppend.type === 'cancellation-confirmation') {
              this._bookingService.loadBookings('upcoming').subscribe({
                error: (err) => console.error('Failed to refresh bookings after confirmation', err),
              });
            }
            this.adjustScrollForLastResponse();
            this.answerGenerating.set(false);
            this._changeDetect.markForCheck();
          } else if (message.event === 'QUESTION_TEXT') {
            this.chatPrompt.set((message.data as string).trim());
            this.showInputBox();
            this.submittingAudio.set(false);
            this.closeRecording();
            // this.getChatResponse('')
          } else if (message.event === 'ANSWER_LIMIT_EXCEEDED') {
            this.loopMessages[this.loopMessages.length - 1].loading = false;
            this.loopMessages[this.loopMessages.length - 1].showProcessingText = false;
            this.publicLimitReached.set(true);
            console.log('ANSWER_LIMIT_EXCEEDED', message);
          }  else if (message.event === 'ANSWER_END') {
            const endData = message.data as Record<string, unknown>;
            if (endData && endData['answerId']) {
              this.loopMessages[this.loopMessages.length - 1].answerId = endData['answerId'] as string;
              this.loopMessages[this.loopMessages.length - 1].id = endData['questionId'] as string;
            }
            this.loopMessages[this.loopMessages.length - 1].answerProcessing = false;
            this.buffer = '';
            this.processingText.set('');
            this.answerProcessing.set(false);
            this.adjustScrollForLastResponse();
            // Refocus the textarea after receiving answer (desktop only)
            // this.focusInput();
            this._changeDetect.markForCheck();
            this._chatService.checkQuestionLimit();
          }
        },
        error: (error) => {
          console.error('WebSocket message error:', error);
          this.messageSubscribed.set(false);
        }
      })
      );

      this._subscription.add(
        this._chatService.endMessages().subscribe(message => {
          this.buffer = '';
        })
      )
    }
  }

  private updateMessages(message: string): void {
    this.loopMessages[this.loopMessages.length - 1].answer = message;
    this.loopMessages[this.loopMessages.length - 1].loading = false;
    this.answerGenerating.set(false);
    this._changeDetect.markForCheck();
    // this.makeElementDraggable(this.draggable.nativeElement);
  }

  setCookieIfNeeded(): Observable<void> {
    if (this._authService.isSignedIn()) {
      return EMPTY.pipe(startWith(void 0)); // Ensure next() is triggered
    }

    const currentTime = Date.now();
    if (currentTime - this.lastCookieSetTime < this.cookieExpiryDuration) {
      return EMPTY.pipe(startWith(void 0)); // Ensure next() is triggered
    }

    return this._cookieService.setCookie().pipe(
      tap(() => {
        this.lastCookieSetTime = currentTime;
      }),
      map(() => void 0)
    );
  }

  toggleSpeaker(message: chat, index: number) {
    if (message.listening) {
      this.stopListening(index);
      return;
    }
    if (this.answerListening()) {
      const playingIndex = this.loopMessages.findIndex(m => m.listening);
      if (playingIndex !== -1) {
        this.stopListening(playingIndex);
      }
      this.answerListening.set(false);
      this.audioAnswerLoading.set(false);
      this.loopMessages.forEach(m => { m.listening = false; m.loadingAudio = false; });
    }
    this.hearAnswer(message.answer, index);
  }

  hearAnswer(text: string, index: number) {
    let formattedText = '';
    const target = this.loopMessages[index];
    if (target.isBlocks && target.contentBlocks) {
      formattedText = this.convertToSpeechText(target.contentBlocks);
    } else {
      formattedText = text;
    }
    if (!formattedText?.trim()) return;
    this.audioAnswerLoading.set(true);
    target.listening = true;
    target.loadingAudio = true; // Set loading flag for this specific message
    this.answerListening.set(true);
    const id = uuidv4();
    target.refId = id;
    const sent = this._chatService.textToAudio(formattedText, id);
    if (!sent) {
      this.audioAnswerLoading.set(false);
      target.listening = false;
      target.loadingAudio = false;
      this.answerListening.set(false);
      this._toaster.warning('Audio unavailable — please retry in a moment', this._translatePipe.transform('Warning'));
    }
    this._changeDetect.markForCheck();
  }

  stopListening(index: number) {
    const target = this.loopMessages[index];
    if (target?.refId) {
      this._chatService.stopThisAudio(target.refId);
    }
    if (target) {
      target.listening = false;
      target.loadingAudio = false;
    }
    this.answerListening.set(false);
    this.audioAnswerLoading.set(false);
    this._changeDetect.markForCheck();
  }

  convertToSpeechText(blocks: ContentBlock[]): string {
    let speechText = '';

    for (const block of blocks) {
      switch (block.type) {
        case 'paragraph':
          speechText += `${block.text}\n\n`;
          break;

        case 'heading':
          speechText += `${(block.text ?? '').toUpperCase()}\n\n`;
          break;

        case 'list':
          const listItems = (block.items ?? []).map((item: unknown) =>
            `- ${item}`
          );
          speechText += listItems.join('\n') + '\n\n';
          break;

        case 'quote':
          speechText += `Quote: "${block.text}"`;
          if (block.author) {
            speechText += ` — ${block.author}`;
          }
          speechText += `\n\n`;
          break;

        case 'link':
          speechText += `${block.text}. You can visit the link at: ${block.url}\n\n`;
          break;

        case 'divider':
          speechText += `---\n\n`;
          break;

        case 'image':
          speechText += `An image is displayed: ${block.alt || 'No description provided.'}\n\n`;
          break;

        case 'table':
          speechText += `A table titled "${block.caption || 'Table'}" is displayed on the screen. Please refer to it for more details.\n\n`;
          break;

        case 'chart':
          speechText += `A chart titled "${block.title}" is displayed on the screen. Please refer to it for data insights.\n\n`;
          break;

        case 'tee-time-picker':
          speechText += this.pickerToSpeech(block.data as any) + '\n\n';
          break;

        case 'tee-time-booking-form':
          speechText += this.bookingFormToSpeech(block.data as any) + '\n\n';
          break;

        case 'tee-time-list':
          speechText += this.listToSpeech(block.data as any) + '\n\n';
          break;

        case 'booking-review':
          speechText += this.reviewToSpeech(block.data as any) + '\n\n';
          break;

        case 'booking-confirmation':
          speechText += this.confirmationToSpeech(block.data as any) + '\n\n';
          break;

        case 'cancellation-confirmation':
          speechText += this.cancellationToSpeech(block.data as any) + '\n\n';
          break;

        case 'waitlist-review':
          speechText += this.waitlistReviewToSpeech(block.data as any) + '\n\n';
          break;

        case 'waitlist-confirmation':
          speechText += this.waitlistConfirmationToSpeech(block.data as any) + '\n\n';
          break;

        default:
          // fallback for unhandled types
          break;
      }
    }

    return speechText.trim();
  }

  private pickerToSpeech(d: any): string {
    if (!d) return '';
    const parts: string[] = [];
    if (d.message) parts.push(d.message);
    const course = d.selection?.courseName ?? d.courseName;
    const date = d.selection?.date ?? d.date;
    const players = d.selection?.players ?? d.players;
    const holes = d.selection?.holes ?? d.holes;
    if (course) {
      const detailBits = [
        date ? `on ${date}` : '',
        players ? `for ${players} players` : '',
        holes ? `${holes} holes` : '',
      ].filter(Boolean).join(', ');
      parts.push(`Tee times at ${course}${detailBits ? ' ' + detailBits : ''}.`);
    }
    const recommended = (d.recommendedTimes ?? []).map((p: any) => p.time).filter(Boolean);
    if (recommended.length) {
      parts.push(`Recommended times: ${recommended.join(', ')}.`);
    }
    return parts.join(' ');
  }

  private bookingFormToSpeech(d: any): string {
    const parts: string[] = [];
    if (d?.error) parts.push(d.error);
    parts.push(d?.message || `Let's find you a tee time!`);
    parts.push(d?.subtitle || 'Please select a place, date, players and holes to search for available tee times.');
    return parts.join(' ');
  }

  /**
   * When the backend returns an empty tee-time-list (no courses found), surface it
   * as an editable booking form with an inline validation message instead of a
   * dead-end "no results" answer. The user can tweak place/date/etc. and re-submit.
   */
  private maybeConvertEmptyListToBookingForm(block: ContentBlock): ContentBlock {
    if (block?.type !== 'tee-time-list') return block;
    const listData = block.data as Record<string, unknown> | undefined;
    const courses = (listData?.['courses'] ?? []) as unknown[];
    if (courses.length > 0) return block;

    const selection = (listData?.['selection'] ?? {}) as Record<string, unknown>;
    return {
      type: 'tee-time-booking-form',
      data: {
        message: `Let's find you a tee time!`,
        subtitle: 'Select a date, player and holes',
        error: (listData?.['message'] as string) || 'No courses found. Try a different place or wider area.',
        selection,
      },
    } as unknown as ContentBlock;
  }

  private listToSpeech(d: any): string {
    if (!d) return '';
    const parts: string[] = [];
    if (d.message) parts.push(d.message);
    const date = d.selection?.date ?? d.date;
    const players = d.selection?.players ?? d.players;
    const holes = d.selection?.holes ?? d.holes;
    if (date || players || holes) {
      const meta = [
        date ? `on ${date}` : '',
        players ? `for ${players} players` : '',
        holes ? `${holes} holes` : '',
      ].filter(Boolean).join(', ');
      parts.push(`Available tee times ${meta}.`);
    }
    const courses = (d.courses ?? []) as any[];
    courses.forEach((c) => {
      const slotCount = c.slotCount ?? c.slots?.length ?? 0;
      const priceRange = c.priceRange ? ` ranging ${c.priceRange}` : '';
      parts.push(`${c.name}: ${slotCount} tee times${priceRange}.`);
    });
    return parts.join(' ');
  }

  private reviewToSpeech(d: any): string {
    if (!d) return '';
    const parts: string[] = [];
    if (d.message) parts.push(d.message);
    const course = d.course?.name ?? '';
    const time = d.teeTime?.time ?? '';
    const date = d.teeTime?.date ?? '';
    const players = d.teeTime?.players;
    const holes = d.teeTime?.holes;
    parts.push(`Please review your booking at ${course} on ${date} at ${time}, ${players} players, ${holes} holes.`);
    const totalDue = d.costSummary?.totalDue;
    const currency = d.costSummary?.currency ?? 'USD';
    if (totalDue !== undefined) {
      parts.push(`Total due is ${totalDue} ${currency}.`);
    }
    parts.push('Tap Confirm to proceed.');
    return parts.join(' ');
  }

  private confirmationToSpeech(d: any): string {
    if (!d) return '';
    const parts: string[] = [];
    if (d.message) parts.push(d.message);
    const course = d.course?.name ?? '';
    const time = d.teeTime?.time ?? '';
    const date = d.teeTime?.date ?? '';
    parts.push(`Your tee time at ${course} on ${date} at ${time} is ${d.status ?? 'confirmed'}.`);
    if (d.confirmationNumber) parts.push(`Confirmation number: ${d.confirmationNumber}.`);
    if (d.nextSteps?.length) {
      parts.push('Next steps:');
      d.nextSteps.forEach((s: string) => parts.push(s));
    }
    return parts.join(' ');
  }

  private cancellationToSpeech(d: any): string {
    if (!d) return '';
    const parts: string[] = [];
    if (d.message) parts.push(d.message);
    const course = d.course?.name ?? '';
    const time = d.teeTime?.time ?? '';
    const date = d.teeTime?.date ?? '';
    if (course && time && date) {
      parts.push(`Your booking at ${course} on ${date} at ${time} has been cancelled.`);
    }
    if (d.nextSteps?.length) {
      parts.push('Next steps:');
      d.nextSteps.forEach((s: string) => parts.push(s));
    }
    return parts.join(' ');
  }

  private waitlistReviewToSpeech(d: any): string {
    if (!d) return '';
    const parts: string[] = [];
    if (d.message) parts.push(d.message);
    const course = d.course?.name ?? '';
    const date = d.teeTime?.date ?? '';
    const players = d.teeTime?.players;
    const holes = d.teeTime?.holes;
    if (course) {
      const meta = [
        date ? `on ${date}` : '',
        players ? `for ${players} players` : '',
        holes ? `${holes} holes` : '',
      ].filter(Boolean).join(', ');
      parts.push(`Review your waitlist for ${course}${meta ? ' ' + meta : ''}.`);
    }
    if (d.costSummary?.bookingFee != null) {
      parts.push(`Booking fee: ${d.costSummary.bookingFee} ${d.costSummary.currency ?? ''}.`.trim());
    }
    return parts.join(' ');
  }

  private waitlistConfirmationToSpeech(d: any): string {
    if (!d) return '';
    const parts: string[] = [];
    if (d.message) parts.push(d.message);
    const course = d.course?.name ?? '';
    const date = d.teeTime?.date ?? '';
    const status = d.status ?? 'waitlisted';
    if (course) {
      parts.push(`Your waitlist for ${course}${date ? ' on ' + date : ''} is ${status}.`);
    }
    if (d.confirmationNumber) parts.push(`Confirmation number: ${d.confirmationNumber}.`);
    if (d.nextSteps?.length) {
      parts.push('Next steps:');
      d.nextSteps.forEach((s: string) => parts.push(s));
    }
    return parts.join(' ');
  }

  startChat(questionModel: AskQuestionModel, apiCall = true): void {
    if (questionModel.textQuestion.trim() || questionModel.audioQuestion) {
      this.loopMessages.push({ question: questionModel.textQuestion, answer: '', loading: true, id: '' });
      if (this.textOnly() && apiCall) {
          this.askQuestion(questionModel, true);
          this.receivingMessage.set(false); // Mark the end of receiving the previous message
      } else {
        this.askQuestion(questionModel, true);
        this.receivingMessage.set(false); // Mark the end of receiving the previous message
      }
    } else {
      this._toaster.error(this._translatePipe.transform("QuestionRequired"), this._translatePipe.transform("Error"));
    }
  }

  onTeeTimeAction(payload: TeeTimeActionPayload) {
    // Cache so verification-required flows can resume the original search
    // (BE doesn't include the source action in the verification block).
    this._sharedService.lastTeeTimeAction.set(payload.action);
    const questionModel = new AskQuestionModel();
    questionModel.chatId = this.chatId();
    questionModel.textQuestion = payload.question;
    questionModel.behaviour = 'clicked';
    questionModel.action = payload.action;
    this.askQuestion(questionModel);
  }

  /**
   * Open the tee-time search form locally without hitting the API.
   * The actual API request happens once the user fills the form and taps Search,
   * which routes through onTeeTimeAction → askQuestion like any other action.
   */
  private openLocalTeeTimeBookingForm(): void {
    const last = this.loopMessages[this.loopMessages.length - 1];
    const lastIsOpenForm = !!last
      && last.isBlocks
      && (last.contentBlocks ?? []).some(b => b.type === 'tee-time-booking-form')
      && !last.loading;
    if (!lastIsOpenForm) {
      const formBlock: ContentBlock = { type: 'tee-time-booking-form', data: {} };
      this.loopMessages.push({
        question: 'Let’s book a tee time',
        answer: '',
        loading: false,
        id: `local-form-${uuidv4()}`,
        isBlocks: true,
        contentBlocks: [formBlock],
      });
    }
    // Don't call setOpenChatBox(): it sets hideLoadingText=true which expands the
    // textarea. We want the input to stay collapsed until the user actually types.
    this.openMobileChatBoxIfNeeded();
    this.autoScrollEnabled = true;
    this._changeDetect.markForCheck();
    if (this.isBrowser) {
      setTimeout(() => this.scrollToBottom(), 0);
    }
  }

  askQuestion(question: AskQuestionModel, isFirst = false, regenerate = false) {
    if ((question.textQuestion.trim() && !this.answerGenerating()) || question.audioQuestion) {
      this.answerGenerating.set(true);
      // Ensure we're subscribed to the answer stream before emitting. Tee-time
      // flows (openLocalTeeTimeBookingForm → onTeeTimeAction) reach askQuestion
      // without ever going through the sharedService.question handler that would
      // otherwise call getMessages(). Without this, the socket answer events
      // (ANSWER_OBJECT / ANSWER_TEXT_CHUNK) are never received and the message
      // stays stuck on its loading state (e.g. "Searching tee times").
      // getMessages() is idempotent via the messageSubscribed guard.
      this.getMessages();
      if (!isFirst) {
        // Show a context-specific processing label while a tee-time search runs
        // (CGA-2959 item 3) instead of echoing the raw "Search tee times" question.
        const loadingText = question.action?.type === 'submit_booking_form'
          ? this._translatePipe.transform('SearchingTeeTimes')
          : undefined;
        this.loopMessages.push({ question: question.textQuestion, separation: question.separation, answer: '', loading: true, id: '', showProcessingText: true, loadingText });
      }
      this.followUpQuestion.set('');
      if (this.textOnly() && !this.isMyBag()) {
        this._chatService.askQuestion(this.chatId(), question.textQuestion, regenerate, question.behaviour, this.isSignedIn(), question.action);
      } else {
        this.medias = [];
        question.mediaKeys.forEach((mediaKey: unknown) => {
          const media = mediaKey as { get: { url: string }; filename: string; mimetype: string };
          const item: MediaModel = {
            id: '',
            url: media.get.url,
            type: '',
            name: media.filename
          };
          if (media.mimetype.includes('image')) {
            item.type = 'image'
          } else if (media.mimetype.includes('video')) {
            item.type = 'video'
          } else {
            item.type = 'document';
          }
          this.medias.push(item);
        });
        if (this.isMyBag()) {
          question.regenerate = regenerate;
          question.chatId = this.chatId();
        }
        this._chatService.askQuestionV2(question);
      }
      // if(!this.chatId){
      //   this._chatService.startChat(question.textQuestion).subscribe((res) => {
      //     this.chatId = res.chatId;
      //     this._chatService.askQuestion(this.chatId, question.textQuestion, regenerate);
      //     this.getMessages();
      //   })
      // } else{

      // }

      setTimeout(() => {
        this.scrollToBottom();
      }, 100);
      this._changeDetect.markForCheck();
    }

    // const messageBody = { chatId, question };
    // this.socket.emit('askQuestion', messageBody);
  }

  scrollToLastHeader() {
    if (this.headers && this.headers.last) {
      const headerElement = this.headers.last.nativeElement;
      headerElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setTimeout(() => {
        const containerElement = document.querySelector('.answer_cont');
        if (containerElement) {
          containerElement.scrollTop = headerElement.offsetTop;
        }
      }, 300);
    }
  }

  openRuleAssistModal(content: TemplateRef<unknown>, index: number) {
    this.ruleAssistQuestionObject = this.loopMessages[index];
    this.ruleAssistModalRef = this._modalService.open(content, {
      backdrop: 'static',
      centered: true,
      windowClass: 'modal-rule-assist'
    });
  }

  closeRuleAssistModal() {
    this.ruleAssistModalRef?.dismiss();
  }

  ngAfterViewChecked(){
    if (this.scrollContainer && this.isBrowser){
      
    }
  }


  private scrollToBottom(): void {
    if(this.isBrowser){
      const el = this.scrollContainer.nativeElement;
      el.scrollTop = el.scrollHeight;
    }
  }
  onUserScroll(): void {
    if (this.isBrowser) {
      const el = this.scrollContainer.nativeElement;
      const scrollBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
  
      // If user scrolled up more than 100px from bottom, disable auto-scroll
      this.autoScrollEnabled = scrollBottom <= 100;
    }
  }

  private adjustScrollForLastResponse(): void {
    if (!this.isBrowser || !this.autoScrollEnabled) return;
  
    const el = this.scrollContainer.nativeElement;
    const responseRows = el.querySelectorAll('.response_row');
    
    if (responseRows.length >= 2) {
      // Get the last answer (response) row - should be the last element
      const lastAnswerRow = responseRows[responseRows.length - 1] as HTMLElement;
      const containerHeight = el.clientHeight;
      
      // Find the answer content area within the last response
      const answerContent = lastAnswerRow.querySelector('.answerBlck__contArea') as HTMLElement;
      
      if (answerContent) {
        const answerTop = lastAnswerRow.offsetTop;
        
        // Position so the start of the answer appears at 30% from top of viewport
        // This leaves room to see previous context while highlighting the new response
        const targetScrollPosition = answerTop - (containerHeight * 0.3);
        
        el.scrollTo({
          top: Math.max(0, targetScrollPosition),
          behavior: 'smooth'
        });
      }
    }
  }
  
  

  addReaction(reaction: string, index: number, messageId: string) {
    if(this.loopMessages[index].reaction === reaction){
      this.loopMessages[index].reaction = '';
      reaction = 'none';
    } else {
      this.loopMessages[index].reaction = reaction;
    }

    this._chatService.addReactionTotheMessage(messageId, reaction).subscribe(res => {res});
  }

  // voice to chat functions

  async startRecording() {
    this.chatPrompt.set('');
    this.hideProcessingText();
  
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

    // Setup MediaRecorder (optional for recording)
    this.mediaRecorder = new MediaRecorder(stream);
    this.audioChunks = [];
    this.base64Audio.set(null);

    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) this.audioChunks.push(event.data);
    };

    this.mediaRecorder.onstop = () => {
      this.audioBlob = new Blob(this.audioChunks, { type: 'audio/wav' });
      this.audioUrl.set(URL.createObjectURL(this.audioBlob));
      this.audioRef.nativeElement.src = this.audioUrl()!;

      const audio = this.audioRef.nativeElement;

      audio.src = this.audioUrl()!;
      audio.load();
      this._changeDetect.markForCheck();


      audio.addEventListener('canplaythrough', () => {
        this.recordingFinished.set(true);
      }, { once: true }); // <- This ensures it only runs once
      // this.convertToBase64(audioBlob);
      stream.getTracks().forEach(track => track.stop());
      if (this.audioContext && this.audioContext.state !== 'closed') {
        this.audioContext.close().catch(err => console.error('Error closing AudioContext:', err));
      }
      if (this.animationFrameId !== null) {
        cancelAnimationFrame(this.animationFrameId);
      }
    };

    this.mediaRecorder.start();
    this.isRecording.set(true);
    this.recordingStatus.set('Recording...');
    this.showEqualizer.set(true);

    // Setup Web Audio API
    this.audioContext = new AudioContext();
    const source = this.audioContext.createMediaStreamSource(stream);
    this.analyser = this.audioContext.createAnalyser();
    this.analyser.fftSize = 64;

    const bufferLength = this.analyser.frequencyBinCount;
    this.dataArray = new Uint8Array(bufferLength);

    source.connect(this.analyser);

    this.animateEqualizer();
  }

  // Call this when user clicks play
  startPlaybackEqualizer() {
    if (!this.audioContext || this.audioContext.state === 'closed') {
      this.audioContext = new AudioContext();
      this.mediaElementSource = null; // Reset, since context is new
    }

    const audioElement = this.audioRef.nativeElement;

    if (!this.mediaElementSource) {
      this.mediaElementSource = this.audioContext.createMediaElementSource(audioElement);

      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 64;
      const bufferLength = this.analyser.frequencyBinCount;
      this.dataArray = new Uint8Array(bufferLength);

      this.mediaElementSource.connect(this.analyser);
      this.analyser.connect(this.audioContext.destination);
    }

    this.animateEqualizer();
  }

  stopPlaybackEqualizer() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    // Optionally clear the canvas
    // const canvas = this.canvasRef.nativeElement;
    // const ctx = canvas.getContext('2d');
    // ctx!.clearRect(0, 0, canvas.width, canvas.height);
  }

  async onPlay() {
    this.showEqualizer.set(true);
    this.startPlaybackEqualizer();
  
    const audio = this.audioRef.nativeElement;
  
    try {
      // Resume audio context if suspended
      if (this.audioContext?.state === 'suspended') {
        await this.audioContext.resume();
      }
  
      audio.muted = false;
      audio.volume = 1;
  
      await audio.play();
    } catch (err) {
      console.error('Playback error:', err);
    }
  }

  onPause() {
    const audioElement = this.audioRef?.nativeElement;
    if (audioElement) {
      audioElement.pause();
      this.onPauseOrEnd();
    }
  }

  onAudioPlay() {
    this.isAudioPlaying.set(true);
    console.log(this.isAudioPlaying());
  }

  onPauseOrEnd() {
    this.isAudioPlaying.set(false);
    this.stopPlaybackEqualizer();
  }

  animateEqualizer() {
    const canvas = this.canvasRef.nativeElement;
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    const centerY = height / 2;

    // Colours are no longer course-driven; the equaliser always uses the light-background fill.
    const fillColor = '#000000';

    // Waveform visualization properties
    const barWidth = 3; // Width of each bar
    const barSpacing = 3; // Space between bars (same as bar width)
    const totalBarWidth = barWidth + barSpacing;
    const numBars = Math.floor(width / totalBarWidth);
    const maxBarHeight = height * 0.6; // Decreased height
    const minBarHeight = 2;
    const dotSpacing = 12; // Increased spacing between dots
    const dotSize = 3; // Increased dot width
    
    // Audio history for waveform
    const waveformData: number[] = new Array(numBars).fill(0);
    const audioLevels: number[] = new Array(numBars).fill(0); // Store raw audio levels
    
    // Animation timing control
    let frameCount = 0;
    const updateFrequency = 2; // Update every 2 frames (slightly slower)
    
    const draw = () => {
      this.animationFrameId = requestAnimationFrame(draw);
      
      frameCount++;
      
      // Update waveform data every frame for high sensitivity
      if (frameCount % updateFrequency === 0) {
        // Get audio frequency data
        this.analyser.getByteFrequencyData(this.dataArray!);
        
        // Calculate average audio level
        const average = this.dataArray!.reduce((sum: number, value: number) => sum + value, 0) / this.dataArray!.length;
        const normalizedLevel = (average / 255);
        
        // Shift existing data to the left (like a scrolling waveform)
        for (let i = 0; i < waveformData.length - 1; i++) {
          waveformData[i] = waveformData[i + 1];
          audioLevels[i] = audioLevels[i + 1];
        }
        
        // Add new audio data to the right with slightly reduced height sensitivity
        const hasAudio = normalizedLevel > 0.005; // Lower threshold for detection
        const intensityMultiplier = 1.5; // Slightly reduced intensity
        const newBarHeight = hasAudio ? 
          minBarHeight + (Math.pow(normalizedLevel, 0.8) * (maxBarHeight - minBarHeight) * intensityMultiplier) : 
          0;
        
        waveformData[waveformData.length - 1] = Math.min(newBarHeight, maxBarHeight);
        audioLevels[audioLevels.length - 1] = normalizedLevel;
      }
      
      // Clear canvas
      ctx!.clearRect(0, 0, width, height);
      
      // Check if there's any recent audio activity (stricter check for transition)
      const recentAudioActivity = audioLevels.some(level => level > 0.005);
      const hasSignificantBars = waveformData.some(height => height > minBarHeight);
      
      if (recentAudioActivity || hasSignificantBars) {
        // Set drawing style for bars
        ctx!.fillStyle = fillColor;
        
        // Draw waveform bars with rounded edges
        for (let i = 0; i < numBars; i++) {
          const x = i * totalBarWidth;
          const barHeight = waveformData[i];
          
          if (barHeight > minBarHeight) {
            // Draw rounded vertical bar centered on centerY
            const radius = barWidth / 2; // Radius is half the bar width
            const barTop = centerY - barHeight / 2;
            const barBottom = centerY + barHeight / 2;
            
            ctx!.beginPath();
            ctx!.moveTo(x + radius, barTop);
            ctx!.lineTo(x + barWidth - radius, barTop);
            ctx!.arc(x + barWidth - radius, barTop + radius, radius, -Math.PI/2, 0);
            ctx!.lineTo(x + barWidth, barBottom - radius);
            ctx!.arc(x + barWidth - radius, barBottom - radius, radius, 0, Math.PI/2);
            ctx!.lineTo(x + radius, barBottom);
            ctx!.arc(x + radius, barBottom - radius, radius, Math.PI/2, Math.PI);
            ctx!.lineTo(x, barTop + radius);
            ctx!.arc(x + radius, barTop + radius, radius, Math.PI, -Math.PI/2);
            ctx!.closePath();
            ctx!.fill();
          } else {
            // Draw small dot when bar height is minimal
            ctx!.beginPath();
            ctx!.arc(x + barWidth/2, centerY, 1, 0, Math.PI * 2);
            ctx!.fill();
          }
        }
      } else {
        // Draw dotted line when no audio
        ctx!.fillStyle = fillColor;
        
        for (let x = dotSpacing; x < width; x += dotSpacing) {
          ctx!.beginPath();
          ctx!.arc(x, centerY, dotSize, 0, Math.PI * 2);
          ctx!.fill();
        }
      }
    };
    
    draw();
  }

  drawRoundedRect(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number
  ) {
    const r = Math.min(radius, height / 2, width / 2); // Avoid over-rounding
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + width - r, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + r);
    ctx.lineTo(x + width, y + height - r);
    ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
    ctx.lineTo(x + r, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
    ctx.fill();
  }

  stopRecording() {
    if (this.mediaRecorder && this.isRecording()) {
      this.mediaRecorder.stop(); // triggers `onstop` where cleanup already happens
      this.isRecording.set(false);
      this.recordingFinished.set(true);
      this.recordingStatus.set('Not recording');
      this.submittingAudio.set(true);
      this.submitAudioQuestion();
    }

    // Ensure we stop the visualizer animation
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }

    // Close audio context if still open
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close();
    }
  }

  closeRecording() {
    this.isRecording.set(false);
    this.recordingFinished.set(false);
    this.showEqualizer.set(false);
    this.audioUrl.set(null);
    this.base64Audio.set(null);
    this.audioBlob = undefined;

    // Stop playback if any
    if (this.audioRef) {
      this.audioRef.nativeElement.pause();
      this.audioRef.nativeElement.src = ''; // Fully unload audio
      this.audioRef.nativeElement.load();
    }
  
    // Stop equalizer animation
    this.stopPlaybackEqualizer();
  
    // Disconnect mediaElementSource if exists
    if (this.mediaElementSource) {
      try {
        this.mediaElementSource.disconnect();
      } catch (e) {
        console.warn('mediaElementSource already disconnected or invalid', e);
      }
      this.mediaElementSource = null;
    }
  
    // Close audioContext if not already closed
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close().then(() => {
        this.audioContext = null;
      });
    }
  }
  

  private convertToBase64(audioBlob: Blob) {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      // Remove the data URL prefix to get only the base64 data
      this.base64Audio.set(base64String.split(',')[1]);
      this._changeDetect.detectChanges();
    };
    reader.readAsDataURL(audioBlob);
  }

  submitAudioQuestion() {
    // call api
    const fileName = this.generateUniqueFileName();
    let uploadedUrl: string | undefined;
    this._mediaService
      .getMediaUrls(fileName)
      .pipe(
        switchMap((response1) => {
          uploadedUrl = response1[0].get?.url;
          return this._mediaService.uploadMediaToS3(response1[0].put?.url!, this.audioBlob!);
        }),
        finalize(() => {})
      )
      .subscribe({
        next: (res) => {
          this.audioBlob = undefined;
          this._chatService.voiceToText(uploadedUrl);
        },
        error: (error) => {
          // this.isProcessing = false;
        }
      });
  }

  generateUniqueFileName(): string {
    const timestamp = Date.now();
    // Generate a random number
    const randomNum = Math.floor(Math.random() * 1e9);
    // Combine timestamp and random number to create a unique identifier
    const uniqueIdentifier = `${timestamp}-${randomNum}`;
    // Construct the unique filename with the file extension
    const uniqueFileName = `${uniqueIdentifier}.mp3`;
    return uniqueFileName;
  }


  loadingMoreChat() {
    this.loadingMoreHistory.set(true);
    this.currentChatPage.update(v => v + 1);
    if (this.isSharedChat()) {
      this.getMessagesFromSharedId();
    } else {
      this.getMessagesFromId();
    }
  }

  resetChat(){
    this.loopMessages = [];
  }

  resetComponentState() {
    // Reset chat-related variables
    this.chatPrompt.set('');
    this.loopMessages = [];
    this.chatId.set('');
    this.currentChatPage.set(1);
    this.totalMessages.set(0);

    // Reset processing states
    this.processing.set(false);
    this.answerProcessing.set(false);
    this.answerGenerating.set(false);
    this.receivingMessage.set(false);
    this.loading.set(false);
    this.chatLoading.set(false);
    this.continueChatProgress.set(false);
    this.loadingMoreHistory.set(false);
    this.hideLoadingText.set(false);

    // Reset audio-related variables
    this.isRecording.set(false);
    this.recordingFinished.set(false);
    this.showEqualizer.set(false);
    this.audioUrl.set(null);
    this.base64Audio.set(null);
    this.audioBlob = undefined;
    this.isAudioPlaying.set(false);
    this.submittingAudio.set(false);
    this.answerListening.set(false);
    this.audioAnswerLoading.set(false);

    // Reset media and UI states
    this.medias = [];
    this.textOnly.set(true);
    this.expandInput.set(false);
    this.openChatBox.set(false);
    this.showSourceModal.set(false);
    this.selectedSources = [];
    this.showLoadMoreBtn.set(false);

    // Reset shared chat states
    this.isSharedChat.set(false);
    this.sharedChatTriggered.set(false);
    this.isNewSharedChat.set(false);

    // Reset question model
    this.question = new AskQuestionModel();
    this.followUpQuestion.set('');

    // Reset flags
    this.isMyBag.set(false);

    // Clear buffer and processing text
    this.buffer = '';
    this.processingText.set(this._translatePipe.transform('Generating...'));
    
    // Close any open modals
    if (this.ruleAssistModalRef) {
      this.ruleAssistModalRef.dismiss();
    }
    if (this.sourceModalRef) {
      this.sourceModalRef.close();
    }
    
    // Stop any ongoing audio
    this.closeRecording();
    
    // Disconnect websocket and unsubscribe from messages
    if (this.messageSubscribed()) {
      this._chatService.disconnectWebSocket();
      this.messageSubscribed.set(false);
    }
    
    // Trigger change detection
    this._changeDetect.markForCheck();
  }

  openSourcePanel(sources: Source[]){
    this.selectedSources = sources;
    this.showSourceModal.set(true);
  }

  openSourceModal(content: TemplateRef<unknown>, sources: Source[]) {
    this.selectedSources = sources;
    this.updateSourceImage();
    this.sourceModalRef = this._modalService.open(content, {
      size: 'md',
      backdrop: 'static',
      centered: true,
    });
  }
  closeSourceModal() {
    if (this.sourceModalRef) {
      this.sourceModalRef.close();
    }
  }

  handleKeyDown(event: KeyboardEvent, textarea: HTMLTextAreaElement) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault(); // Prevent new line
      if (!this.answerProcessing() && this.chatPrompt().trim()) {
        this.getChatResponse();
      }
    }
  }

  focusInput(){
    if(window.innerWidth > 991){
      setTimeout(() => {
        if (this.chatTextareaRef && this.chatTextareaRef.nativeElement) {
          this.chatTextareaRef.nativeElement.focus();
        }
      }, 100);
    }
  }

  setupGlobalKeyListener() {
    document.addEventListener('keydown', (event: KeyboardEvent) => {
      // Check if the key is a letter, number, or space
      const isTypingKey = /^[a-zA-Z0-9\s]$/.test(event.key);
      
      // Don't trigger if user is already typing in an input/textarea or if special keys are pressed
      const activeElement = document.activeElement as HTMLElement;
      const isInputFocused = activeElement?.tagName === 'INPUT' || 
                           activeElement?.tagName === 'TEXTAREA' || 
                           (activeElement as HTMLElement)?.contentEditable === 'true';
      
      // Don't trigger on modifier keys or special keys
      const isModifierKey = event.ctrlKey || event.altKey || event.metaKey;
      
      if (isTypingKey && !isInputFocused && !isModifierKey && !this.answerProcessing()) {
        // Show the input box and focus it
        this.showInputBox();

        // Add the typed character to the chat prompt
        this.chatPrompt.update(v => v + event.key);
        
        // Focus the textarea after a short delay
        setTimeout(() => {
          if (this.chatTextareaRef?.nativeElement) {
            this.chatTextareaRef.nativeElement.focus();
            // Move cursor to end
            const textarea = this.chatTextareaRef.nativeElement;
            textarea.setSelectionRange(textarea.value.length, textarea.value.length);
          }
        }, 50);
        
        this._changeDetect.markForCheck();
      }
    });
  }

  setupIOSKeyboardFix() {
    // Check if it's iOS Safari or Android
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);
    
    if (isIOS || isSafari || isAndroid) {
      // Set initial viewport height
      const setViewportHeight = () => {
        const vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty('--vh', `${vh}px`);
      };

      // Set initial value
      setViewportHeight();

      // Handle viewport changes (keyboard open/close)
      let resizeTimer: ReturnType<typeof setTimeout>;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          setViewportHeight();
        }, 100);
      });

      // Handle input focus for iOS and Android - use a more robust approach
      this.setupInputFocusHandlers(isAndroid, isIOS);

      // Prevent zoom on input focus for iOS
      const preventZoom = (e: Event) => {
        const target = e.target as HTMLElement;
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
          target.style.fontSize = '16px';
        }
      };

      document.addEventListener('focusin', preventZoom);
      document.addEventListener('focusout', () => {
        // Reset font size after focus out
        setTimeout(() => {
          if (this.chatTextareaRef?.nativeElement) {
            this.chatTextareaRef.nativeElement.style.fontSize = '';
          }
        }, 100);
      });
    }
  }

  setupInputFocusHandlers(isAndroid: boolean, isIOS: boolean) {
    // Use event delegation to handle focus on input that might be recreated
    document.addEventListener('focusin', (event) => {
      const target = event.target as HTMLElement;
      if (target && target === this.chatTextareaRef?.nativeElement) {
        setTimeout(() => {
          if (this.chatTextareaRef?.nativeElement) {
            if (isAndroid) {
              // For Android, scroll input into view and add extra space
              this.chatTextareaRef.nativeElement.scrollIntoView({
                behavior: 'smooth',
                block: 'end'
              });
              // Add extra bottom padding to ensure input is visible
              document.body.style.paddingBottom = '300px';
              
              // Also scroll the chat container to bottom
              const chatContainer = document.querySelector('.chat-box-container');
              if (chatContainer) {
                chatContainer.scrollTop = chatContainer.scrollHeight;
              }
            } else if (isIOS) {
              // For iOS
              this.chatTextareaRef.nativeElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
              });
            }
          }
        }, isAndroid ? 100 : 300);
      }
    });

    // Handle blur event to remove extra padding
    document.addEventListener('focusout', (event) => {
      const target = event.target as HTMLElement;
      if (target && target === this.chatTextareaRef?.nativeElement && isAndroid) {
        setTimeout(() => {
          document.body.style.paddingBottom = '';
        }, 100);
      }
    });
  }

  
  ngOnDestroy(): void {
    const playingIndex = this.loopMessages.findIndex(item => item.listening);
    if (playingIndex !== -1) {
      this.stopListening(playingIndex)
    }
    this._chatService.disconnectWebSocket();
    this._subscription.unsubscribe();
    this.messageSubscribed.set(false);
    this._chatService.publicQuestionHistory.set([]);
    this.loopMessages = [];
    this._sharedService.setQuestion(new AskQuestionModel());
    this.publicLimitReached.set(false);
  }

}
